import React, { FC, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { showLoadingAction } from 'src/shared/actions';
import { useAppState } from 'src/shared/appState';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { modeAction, selectMerchantProductAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { merchantProduct_item$data } from '../../state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from '../../storeCatalog/storeCatalog';
import { connectCatalogMutation, connectCatalogMutation$data } from './__generated__/connectCatalogMutation.graphql';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { connectCatalogQuery } from './__generated__/connectCatalogQuery.graphql';

const query = graphql`
    query connectCatalogQuery {
        generalSettings {
            isSideImageScalingEnabled
        }
    }
`;

const ConnectCatalog: FC<{}> = (props) => {
    const data = useLazyLoadQuery<connectCatalogQuery>(query, {});
    const changeStep = useProductConfigurationChangeStep();
    const { state, dispatch } = useProductConfigurationState();
    const { dispatch: appDispatch, state: appState } = useAppState();
    const { showDialog, closeDialog } = useDialogManager();
    const [isSideImageScalingEnabled, setIsSideImageScalingEnabled] = useState<boolean>(data.generalSettings.isSideImageScalingEnabled);

    const [prepareMutation] = useMutation<connectCatalogMutation>(graphql`
        mutation connectCatalogMutation($input: ModelPrepareConfigurationInput!) {
            prepareConfiguration(input: $input) {
                imageUrl
                alreadyConfigured
            }
        }
    `);
    const handleProductClick = async (
        merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>,
        hideAlreadyConfiguredBadge?: boolean
    ) => {
        if (merchantProduct.isConfiguratedInZakeke && !hideAlreadyConfiguredBadge) return;
        appDispatch(showLoadingAction(true));

        const prepareResult = await new Promise<connectCatalogMutation$data>((resolve) => {
            prepareMutation({
                onCompleted: (response) => resolve(response),
                variables: {
                    input: {
                        salesChannelID: state.salesChannelID,
                        code: merchantProduct.sku!,
                        imageUrl: merchantProduct.thumbnail,
                        isSideImageScalingEnabled: isSideImageScalingEnabled
                    }
                }
            });
        });

        const thumbnail = prepareResult.prepareConfiguration.imageUrl
            ? await new Promise<HTMLImageElement>((resolve) => {
                  const image = new Image();
                  image.onload = () => resolve(image);
                  image.src = prepareResult.prepareConfiguration.imageUrl!;
              })
            : null;

        const handleSelectMerchantProduct = () => {
            closeDialog('merchant-product-message'); // Close dialog
            dispatch(selectMerchantProductAction(merchantProduct, thumbnail)); // Select merchant product
            if (appState.lastUsedTool === 1)
                changeStep('print-area'); // Go to next page
            else if (appState.lastUsedTool === 2) {
                dispatch(modeAction('configurator'));
                changeStep('3D-product-configuration-steps');
            } else {
                console.log('attenzione, non dovresti raggiunger questo step');
            }
        };

        if (state.merchantProduct && state.merchantProduct.sku != merchantProduct.sku) {
            showDialog(
                'merchant-product-message',
                <QuestionDialog
                    message={
                        <span>
                            {T._('Selecting a new product on your store will reset your current configuration.', 'Admin')}
                            <br />
                            <br />
                            {T._('Are you sure?', 'Admin')}
                        </span>
                    }
                    onYesClick={handleSelectMerchantProduct}
                />
            );
        } else if (state.merchantProduct && state.merchantProduct.sku == merchantProduct.sku) {
            if (appState.lastUsedTool === 1) changeStep('print-area');
            else if (appState.lastUsedTool === 2) {
                dispatch(modeAction('configurator'));
                changeStep('3D-product-configuration-steps');
            } else {
                console.log('attenzione, non dovresti raggiunger questo step');
            }
        } else {
            handleSelectMerchantProduct();
        }

        appDispatch(showLoadingAction(false));
    };

    return <StoreCatalog showBackButton={false} salesChannelID={state.salesChannelID} onProductClick={handleProductClick} selectedProductsSKUs={[]} />;
};

export default ConnectCatalog;
